<template>
    <div class="nf-wrap">
        <div class="nf-grid">
            <!-- Main card: title + content per language -->
            <section class="nf-card">
                <div class="nf-tabs">
                    <button
                        type="button"
                        class="nf-tab"
                        :class="{ 'is-active': activeLang === 'fr' }"
                        @click="activeLang = 'fr'"
                    >
                        <span class="nf-tab-dot" :class="dotClass('fr')"></span>
                        {{ $t('news.form.tab_fr') }}
                    </button>
                    <button
                        type="button"
                        class="nf-tab"
                        :class="{ 'is-active': activeLang === 'en' }"
                        @click="activeLang = 'en'"
                    >
                        <span class="nf-tab-dot" :class="dotClass('en')"></span>
                        {{ $t('news.form.tab_en') }}
                    </button>
                </div>

                <div class="nf-card-body">
                    <template v-for="lang in LANGS">
                        <div
                            v-show="activeLang === lang"
                            :key="`lang-${lang}`"
                            class="nf-lang-block"
                        >
                            <div class="nf-field">
                                <label class="nf-field-label">
                                    {{ $t('news.form.title') }}
                                    <span v-if="lang === 'fr'" class="nf-field-required">*</span>
                                </label>
                                <input
                                    v-model.trim="form[lang].title"
                                    type="text"
                                    class="nf-input"
                                    :placeholder="$t('news.form.placeholder_title')"
                                />
                            </div>

                            <div class="nf-field">
                                <label class="nf-field-label">
                                    {{ $t('news.form.content') }}
                                </label>
                                <div class="nf-editor">
                                    <ckeditor
                                        v-model="form[lang].content"
                                        :editor="editor"
                                        :config="editorConfig"
                                    />
                                </div>
                            </div>
                        </div>
                    </template>
                </div>
            </section>

            <!-- Sidebar: category + cover -->
            <aside class="nf-side">
                <section class="nf-card">
                    <header class="nf-card-head">
                        <i class="fal fa-tag"></i>
                        <h3>{{ $t('news.form.category') }}</h3>
                    </header>
                    <div class="nf-card-body">
                        <select
                            v-model="form.news_category_id"
                            class="nf-select"
                        >
                            <option :value="null">
                                {{ $t('news.form.category_none') }}
                            </option>
                            <option
                                v-for="cat in categories"
                                :key="cat.id"
                                :value="cat.id"
                            >
                                {{ categoryLabel(cat) }}
                            </option>
                        </select>
                    </div>
                </section>

                <section class="nf-card">
                    <header class="nf-card-head">
                        <i class="fal fa-image"></i>
                        <h3>{{ $t('news.form.cover') }}</h3>
                    </header>
                    <div class="nf-card-body">
                        <NewsCoverUpload
                            :media="form.cover_media"
                            @change="onCoverChange"
                        />
                    </div>
                </section>
            </aside>
        </div>

        <!-- Bottom action bar -->
        <footer class="nf-footer">
            <div class="nf-footer-left">
                <BmvButton
                    v-if="isEdit && canDelete"
                    variant="ghost"
                    icon="fal fa-trash"
                    :disabled="!!busyAction"
                    @click="submit('delete')"
                >
                    {{ $t('common.delete') }}
                </BmvButton>
            </div>

            <div class="nf-footer-right">
                <BmvButton
                    variant="ghost"
                    :disabled="!!busyAction"
                    @click="cancel"
                >
                    {{ $t('common.cancel') }}
                </BmvButton>

                <template v-if="!isEdit">
                    <BmvButton
                        variant="secondary"
                        icon="fal fa-save"
                        :disabled="!isValid || !!busyAction"
                        :loading="busyAction === 'save_draft'"
                        @click="submit('save_draft')"
                    >
                        {{ $t('news.actions.save_draft') }}
                    </BmvButton>
                    <BmvButton
                        variant="success"
                        icon="fal fa-paper-plane"
                        :disabled="!isValid || !!busyAction"
                        :loading="busyAction === 'publish'"
                        @click="submit('publish')"
                    >
                        {{ $t('news.actions.publish') }}
                    </BmvButton>
                </template>

                <template v-else>
                    <BmvButton
                        variant="secondary"
                        icon="fal fa-save"
                        :disabled="!isValid || !!busyAction"
                        :loading="busyAction === 'update'"
                        @click="submit('update')"
                    >
                        {{ $t('news.actions.update') }}
                    </BmvButton>
                    <BmvButton
                        v-if="initial && initial.status === 'published'"
                        variant="secondary"
                        icon="fal fa-ban"
                        :disabled="!!busyAction"
                        :loading="busyAction === 'unpublish'"
                        @click="submit('unpublish')"
                    >
                        {{ $t('news.actions.unpublish') }}
                    </BmvButton>
                    <BmvButton
                        v-else
                        variant="success"
                        icon="fal fa-paper-plane"
                        :disabled="!isValid || !!busyAction"
                        :loading="busyAction === 'publish'"
                        @click="submit('publish')"
                    >
                        {{ $t('news.actions.publish') }}
                    </BmvButton>
                </template>
            </div>
        </footer>
    </div>
</template>

<script>
import ClassicEditor from '@ckeditor/ckeditor5-build-classic'
import BmvButton from '@/components/UI/v2/BmvButton.vue'
import NewsCoverUpload from '@/components/News/NewsCoverUpload.vue'

const LANGS = ['fr', 'en']

function emptyForm() {
    return {
        fr: { title: '', content: '' },
        en: { title: '', content: '' },
        news_category_id: null,
        cover_media: null
    }
}

export default {
    name: 'NewsForm',
    components: { BmvButton, NewsCoverUpload },
    props: {
        mode: {
            type: String,
            default: 'create',
            validator: (v) => ['create', 'edit'].includes(v)
        },
        initial: { type: Object, default: null },
        categories: { type: Array, default: () => [] },
        busyAction: { type: String, default: null },
        canDelete: { type: Boolean, default: false }
    },
    data() {
        return {
            LANGS,
            activeLang: 'fr',
            form: emptyForm(),
            editor: ClassicEditor,
            editorConfig: {
                toolbar: {
                    items: [
                        'heading',
                        '|',
                        'bold',
                        'italic',
                        'link',
                        '|',
                        'bulletedList',
                        'numberedList',
                        'outdent',
                        'indent',
                        '|',
                        'blockQuote',
                        'insertTable',
                        'mediaEmbed',
                        '|',
                        'undo',
                        'redo'
                    ],
                    shouldNotGroupWhenFull: true
                },
                heading: {
                    options: [
                        { model: 'paragraph', title: 'Paragraphe', class: 'ck-heading_paragraph' },
                        { model: 'heading2', view: 'h2', title: 'Titre 2', class: 'ck-heading_heading2' },
                        { model: 'heading3', view: 'h3', title: 'Titre 3', class: 'ck-heading_heading3' },
                        { model: 'heading4', view: 'h4', title: 'Titre 4', class: 'ck-heading_heading4' }
                    ]
                },
                table: {
                    contentToolbar: [
                        'tableColumn',
                        'tableRow',
                        'mergeTableCells'
                    ]
                },
                link: {
                    addTargetToExternalLinks: true,
                    defaultProtocol: 'https://',
                    decorators: {
                        openInNewTab: {
                            mode: 'manual',
                            label: 'Ouvrir dans un nouvel onglet',
                            defaultValue: true,
                            attributes: {
                                target: '_blank',
                                rel: 'noopener noreferrer'
                            }
                        }
                    }
                }
            }
        }
    },
    computed: {
        isEdit() {
            return this.mode === 'edit'
        },
        lang() {
            return this.$store.state.lang
        },
        isValid() {
            return (
                !!this.form.fr &&
                (this.form.fr.title || '').trim().length > 0
            )
        }
    },
    watch: {
        initial: {
            immediate: true,
            handler(next) {
                if (!next) {
                    this.form = emptyForm()
                    return
                }
                this.form = this.fromArticle(next)
            }
        }
    },
    methods: {
        fromArticle(article) {
            const state = emptyForm()
            const translations = article.translations || []
            translations.forEach((t) => {
                if (t && t.locale && state[t.locale]) {
                    state[t.locale].title = t.title || ''
                    state[t.locale].content = t.content || ''
                }
            })
            state.news_category_id =
                article.news_category_id ||
                (article.category && article.category.id) ||
                null
            state.cover_media = article.cover_media || null
            return state
        },
        categoryLabel(cat) {
            if (!cat) return ''
            const translations = cat.translations || []
            const match =
                translations.find((t) => t.locale === this.lang) ||
                translations.find((t) => t.locale === 'fr') ||
                translations[0]
            return (match && (match.name || match.title)) || cat.name || '—'
        },
        dotClass(lang) {
            const block = this.form[lang]
            if (!block) return ''
            const hasTitle = (block.title || '').trim().length > 0
            const hasContent = (block.content || '').trim().length > 0
            if (hasTitle && hasContent) return 'nf-tab-dot--full'
            if (hasTitle || hasContent) return 'nf-tab-dot--partial'
            return ''
        },
        onCoverChange(media) {
            this.$set(this.form, 'cover_media', media || null)
        },
        buildPayload() {
            const payload = {
                fr: {
                    title: this.form.fr.title,
                    content: this.form.fr.content || ''
                }
            }
            const enTitle = (this.form.en.title || '').trim()
            const enContent = (this.form.en.content || '').trim()
            if (enTitle || enContent) {
                payload.en = {
                    title: enTitle,
                    content: this.form.en.content || ''
                }
            }
            if (this.form.news_category_id) {
                payload.news_category_id = this.form.news_category_id
            }
            if (this.form.cover_media && this.form.cover_media.base64) {
                payload.cover_media = this.form.cover_media.base64
            }
            return payload
        },
        submit(action) {
            if (this.busyAction) return
            if (action === 'delete') {
                this.$emit('submit', { action })
                return
            }
            if (
                (action === 'save_draft' ||
                    action === 'publish' ||
                    action === 'update') &&
                !this.isValid
            ) {
                this.activeLang = 'fr'
                return
            }
            this.$emit('submit', {
                action,
                payload: this.buildPayload()
            })
        },
        cancel() {
            this.$emit('cancel')
        }
    }
}
</script>

<style scoped>
.nf-wrap {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.nf-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 320px;
    gap: 16px;
    align-items: start;
}

.nf-side {
    display: flex;
    flex-direction: column;
    gap: 16px;
    position: sticky;
    top: 16px;
    align-self: flex-start;
}

.nf-card {
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    box-shadow: var(--v2-shadow-sm);
    overflow: hidden;
}

.nf-card-head {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    border-bottom: 1px solid var(--v2-border-subtle);
    background: var(--v2-surface-1);
}

.nf-card-head i {
    color: var(--v2-primary);
    font-size: 0.9rem;
}

.nf-card-head h3 {
    margin: 0;
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--v2-text-1);
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.nf-card-body {
    padding: 16px;
}

/* Language tabs */
.nf-tabs {
    display: flex;
    border-bottom: 1px solid var(--v2-border-subtle);
    background: var(--v2-surface-1);
}

.nf-tab {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 18px;
    border: 0;
    background: transparent;
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--v2-text-3);
    cursor: pointer;
    transition: color var(--v2-transition-fast),
        box-shadow var(--v2-transition-fast);
    border-bottom: 2px solid transparent;
    margin-bottom: -1px;
}

.nf-tab:hover {
    color: var(--v2-text-1);
}

.nf-tab.is-active {
    color: var(--v2-primary);
    border-bottom-color: var(--v2-primary);
    background: var(--v2-surface-0);
}

.nf-tab-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--v2-border-strong);
}

.nf-tab-dot--partial {
    background: var(--v2-warning);
}

.nf-tab-dot--full {
    background: var(--v2-success);
}

/* Fields */
.nf-lang-block {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.nf-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.nf-field-label {
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--v2-text-3);
    margin: 0;
}

.nf-field-required {
    color: var(--v2-danger);
    margin-left: 2px;
}

.nf-input {
    height: 40px;
    padding: 0 12px;
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    background: var(--v2-surface-0);
    color: var(--v2-text-1);
    font-size: 0.95rem;
    font-weight: 600;
    transition: border-color var(--v2-transition-fast);
}

.nf-input:focus {
    outline: none;
    border-color: var(--v2-primary);
    box-shadow: var(--v2-shadow-focus);
}

.nf-select {
    width: 100%;
    height: 38px;
    padding: 0 10px;
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    background: var(--v2-surface-0);
    color: var(--v2-text-1);
    font-size: 0.875rem;
    cursor: pointer;
    transition: border-color var(--v2-transition-fast);
}

.nf-select:focus {
    outline: none;
    border-color: var(--v2-primary);
    box-shadow: var(--v2-shadow-focus);
}

/* CKEditor containment */
.nf-editor ::v-deep .ck.ck-editor {
    border: 1px solid var(--v2-border) !important;
    border-radius: var(--v2-radius-md) !important;
    overflow: hidden;
}

.nf-editor ::v-deep .ck.ck-toolbar {
    border: none !important;
    border-bottom: 1px solid var(--v2-border-subtle) !important;
    background: var(--v2-surface-1) !important;
}

.nf-editor ::v-deep .ck.ck-editor__main > .ck-editor__editable {
    border: none !important;
    min-height: 55vh;
    max-height: 70vh;
    padding: 16px !important;
    color: var(--v2-text-1) !important;
    font-family: var(--v2-font-sans);
    font-size: 0.95rem;
    line-height: 1.6;
    overflow-y: auto;
}

/* Typography inside the editor — the global Tailwind/Bootstrap reset
 * strips heading styles, so rebuild them locally. Without this the
 * heading dropdown has no visible effect. */
.nf-editor ::v-deep .ck-content h2 {
    font-size: 1.6rem;
    font-weight: 700;
    line-height: 1.25;
    color: var(--v2-text-1);
    margin: 1.25em 0 0.5em;
}

.nf-editor ::v-deep .ck-content h3 {
    font-size: 1.25rem;
    font-weight: 700;
    line-height: 1.3;
    color: var(--v2-text-1);
    margin: 1em 0 0.5em;
}

.nf-editor ::v-deep .ck-content h4 {
    font-size: 1.05rem;
    font-weight: 600;
    line-height: 1.35;
    color: var(--v2-text-1);
    margin: 1em 0 0.4em;
}

.nf-editor ::v-deep .ck-content p {
    margin: 0 0 0.75em;
}

.nf-editor ::v-deep .ck-content p:last-child {
    margin-bottom: 0;
}

.nf-editor ::v-deep .ck-content ul,
.nf-editor ::v-deep .ck-content ol {
    padding-left: 1.5em;
    margin: 0 0 0.75em;
}

.nf-editor ::v-deep .ck-content ul {
    list-style: disc;
}

.nf-editor ::v-deep .ck-content ol {
    list-style: decimal;
}

.nf-editor ::v-deep .ck-content li {
    margin: 0.2em 0;
}

.nf-editor ::v-deep .ck-content blockquote {
    margin: 1em 0;
    padding: 6px 16px;
    border-left: 3px solid var(--v2-primary);
    color: var(--v2-text-3);
    font-style: italic;
    background: var(--v2-surface-1);
}

.nf-editor ::v-deep .ck-content a {
    color: var(--v2-primary);
    text-decoration: underline;
}

.nf-editor ::v-deep .ck-content a:hover {
    color: var(--v2-primary-hover);
}

.nf-editor ::v-deep .ck-content figure.table {
    margin: 1em 0;
    display: block;
}

.nf-editor ::v-deep .ck-content table {
    border-collapse: collapse;
    width: 100%;
    font-size: 0.9rem;
}

.nf-editor ::v-deep .ck-content table td,
.nf-editor ::v-deep .ck-content table th {
    border: 1px solid var(--v2-border);
    padding: 8px 10px;
    vertical-align: top;
}

.nf-editor ::v-deep .ck-content table th {
    background: var(--v2-surface-1);
    font-weight: 600;
    color: var(--v2-text-1);
}

.nf-editor ::v-deep .ck-content figure.media {
    margin: 1em 0;
}

.nf-editor ::v-deep .ck-content .media,
.nf-editor ::v-deep .ck-content figure.media oembed,
.nf-editor ::v-deep .ck-content figure.media iframe {
    width: 100%;
    aspect-ratio: 16 / 9;
    border: 0;
    border-radius: var(--v2-radius-md);
}

.nf-editor ::v-deep .ck-editor__editable.ck-focused {
    box-shadow: none !important;
}

/* Footer */
.nf-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    box-shadow: var(--v2-shadow-sm);
    position: sticky;
    bottom: 12px;
    z-index: 5;
}

.nf-footer-left,
.nf-footer-right {
    display: inline-flex;
    gap: 8px;
    flex-wrap: wrap;
}

@media (max-width: 900px) {
    .nf-grid {
        grid-template-columns: 1fr;
    }
    .nf-footer {
        flex-direction: column;
        align-items: stretch;
        position: static;
    }
    .nf-footer-left,
    .nf-footer-right {
        justify-content: flex-end;
    }
}
</style>
